import {
  BOOK_OPEN_STATES,
  type BookOpenState,
  type Caption,
  type Cover,
  type Page,
  PageSide,
} from '../../components/PhotoBook/PhotoBookProvider/PhotoBook.types'
import { isDesignedCover } from '../../components/PhotoBook/PhotoBookProvider/PhotoBook.utils'
import {
  type SerializedCover,
  type SerializedPage,
} from '../PhotoBook/photoBookDTO.types'
import { DEFAULT_COVER_TEXT_KEY } from '../PhotoBook/PhotoBook.consts'
import { designedCovers } from '@mixtiles/web-backend-shared'
import { translateManager as t } from 'services/TranslateManager'
import { photosInPages } from '../PhotoBook/photoBookUtils'
import { getBookPageTier } from '../PhotoBook/bookPageTier'
import { type BookSize, type DesignedCover } from '@mixtiles/web-backend-shared'
import { getShortSizeName } from '../SizesManager'
import sumBy from 'lodash/sumBy'
import uniq from 'lodash/uniq'
import { roundDecimal } from '../../utils/utils'
import { calculatePercentage } from './AnalyticsUtils'

// `bookId` is the book instance, distinct from `Product ID`, which names the
// size SKU the book is ordered as.
export function getPhotoBookProps(
  bookId: string | null,
  bookSize: BookSize,
  cover: Cover | null,
  pages: Page[]
): Record<string, unknown> {
  return {
    'Book ID': bookId,
    ...getPhotoBookProductProps(bookSize),
    ...getCoverProps(cover),
    ...getPagesProps(pages),
  }
}

export function getPhotoBookViewProps(
  bookOpenState: BookOpenState,
  pages: Page[],
  pageIndex: number
): Record<string, unknown> {
  let viewMode
  let isLastPage = false
  if (
    bookOpenState === BOOK_OPEN_STATES.CLOSED_ON_BACK ||
    pageIndex >= pages.length
  ) {
    viewMode = 'Back Cover'
  } else if (bookOpenState === BOOK_OPEN_STATES.CLOSED_ON_FRONT) {
    viewMode = 'Front Cover'
  } else {
    viewMode = formatPageIndexRange(pages, pageIndex)
    isLastPage =
      pageIndex === pages.length || pages[pageIndex].back.photos.length === 0
  }
  return {
    'Current Page View': viewMode,
    'Is Last Page': isLastPage,
  }
}

// The price a book is in, named by its size and page limit, e.g. 10x10_40.
export function getBookPriceTier(
  bookSize: BookSize,
  pageCount: number
): string {
  const size = getShortSizeName(bookSize).toLowerCase()
  return `${size}_${getBookPageTier(pageCount)}`
}

export function getPhotoBookProductProps(
  bookSize: BookSize
): Record<string, unknown> {
  let sizeProps
  if (bookSize) {
    const size = getShortSizeName(bookSize).toLowerCase()
    sizeProps = { 'Book Size': size, 'Product ID': size }
  }
  return {
    ...sizeProps,
    'Product Type': 'Photo Books',
    'Category Name': 'Photo Books',
  }
}

const designedCoversById: Record<string, DesignedCover | undefined> =
  designedCovers

function getDesignedCoverProps(
  designedCoverId: string
): Record<string, unknown> {
  const designedCover = designedCoversById[designedCoverId]
  if (!designedCover) return {}

  return {
    'Designed Cover ID': designedCoverId,
    'Designed Cover Name': designedCover.name,
    'Designed Cover Tags': designedCover.tags,
  }
}

function getCoverTitleProps(caption: Caption): Record<string, unknown> {
  if (!caption.text) return {}

  return {
    'Book Title': caption.text,
    'Cover Style': caption.style,
    'Is Default Title': caption.text === t.get(DEFAULT_COVER_TEXT_KEY),
    'Title Length': caption.text.length,
  }
}

export function getCoverProps(cover: Cover | null): Record<string, unknown> {
  if (!cover) return {}

  return {
    'Cover Type': cover.type,
    ...getCoverTitleProps(cover.caption),
    ...(isDesignedCover(cover)
      ? getDesignedCoverProps(cover.designedCoverId)
      : {}),
  }
}

// A serialized cover saved before it carried `type` still reaches orders, so
// its kind is read from what it holds, in the backend's `getCoverType` order.
function getSerializedCoverType(cover: SerializedCover): Cover['type'] {
  if (cover.designedCoverId) return 'designed'
  if (cover.aiGeneratedCoverUrl) return 'ai-generated'
  return 'photo'
}

// Order-event cover props, read off the SerializedCover the order layer
// carries rather than the editor's cover.
export function getOrderCoverProps(
  cover: SerializedCover | null | undefined
): Record<string, unknown> {
  const isAiGenerated = !!cover?.aiGeneratedCoverUrl
  return {
    ...(cover && {
      'Cover Type': getSerializedCoverType(cover),
      ...getCoverTitleProps(cover.caption),
    }),
    'Is AI Generated Designed Cover': isAiGenerated,
    ...(isAiGenerated
      ? { 'Title Used': cover?.aiGeneratedTitle ?? cover?.caption?.text }
      : {}),
    ...(cover?.designedCoverId
      ? getDesignedCoverProps(cover.designedCoverId)
      : {}),
  }
}

// One book as the order layer carries it: the serialized DTO the backend
// receives, not the editor's cover and spreads.
type OrderedPhotoBook = {
  metadata: {
    cover: SerializedCover
    pages: SerializedPage[]
  }
}

// Printed sides that hold photos, and how many of those carry a caption.
// Serialization already dropped the blank sides, so every serialized page is
// one printed side. Captions live on the photo.
function getBookCaptionCounts(pages: SerializedPage[]) {
  return {
    pageCount: pages.length,
    pagesWithCaptionsCount: pages.filter((page) =>
      page.photos.some((photo) => photo?.caption?.text.trim())
    ).length,
  }
}

function getDesignedCoversInOrder(covers: SerializedCover[]) {
  return covers.flatMap(({ designedCoverId }) => {
    if (!designedCoverId) return []

    const designedCover = designedCoversById[designedCoverId]
    return designedCover ? [{ id: designedCoverId, designedCover }] : []
  })
}

// Order-level props covering every book in a photo-book order. Cover fields
// become one entry per book and page counts sum, so a two-book order reports
// both books; each book's own detail rides its Product Ordered event.
export function getOrderPhotoBooksProps(
  books: OrderedPhotoBook[]
): Record<string, unknown> {
  const covers = books.map((book) => book.metadata.cover)
  const designedCoversInOrder = getDesignedCoversInOrder(covers)
  const designedCoverNames = designedCoversInOrder.map(
    ({ designedCover }) => designedCover.name
  )
  const aiCovers = covers.filter((cover) => !!cover.aiGeneratedCoverUrl)
  const captionCounts = books.map((book) =>
    getBookCaptionCounts(book.metadata.pages)
  )
  const pageCount = sumBy(captionCounts, (counts) => counts.pageCount)
  const pagesWithCaptionsCount = sumBy(
    captionCounts,
    (counts) => counts.pagesWithCaptionsCount
  )

  return {
    'Books Count': books.length,
    'Is Creative Cover': covers.some((cover) => !!cover.designedCoverId),
    'Is AI Generated Designed Cover': aiCovers.length > 0,
    ...(designedCoversInOrder.length > 0 && {
      'Creative Cover Location': designedCoverNames,
      'Designed Cover ID': designedCoversInOrder.map(({ id }) => id),
      'Designed Cover Name': designedCoverNames,
      'Designed Cover Tags': uniq(
        designedCoversInOrder.flatMap(({ designedCover }) => designedCover.tags)
      ),
    }),
    ...(aiCovers.length > 0 && {
      'Title Used': aiCovers.map(
        (cover) => cover.aiGeneratedTitle ?? cover.caption.text
      ),
    }),
    'Book Total Pages': pageCount,
    'Book Pages With Captions': pagesWithCaptionsCount,
    'Book Caption Percentage': calculatePercentage(
      pagesWithCaptionsCount,
      pageCount,
      roundDecimal
    ),
  }
}

export function getPagesProps(pages: Page[]): Record<string, unknown> {
  return {
    'Page Count': pages.length,
    'Photo Count': photosInPages(pages).length,
  }
}

// For the first and last pages we want to show the page number, for the rest we want to show the range (starting from 1)
function formatPageIndexRange(pages: Page[], pageIndex: number): string {
  if (pageIndex === 0) {
    return '1'
  } else if (pageIndex === pages.length) {
    return `${2 * pageIndex}`
  } else {
    const startPage = 2 * pageIndex
    const endPage = startPage + 1
    return `${startPage}-${endPage}`
  }
}

export function calculatePageIndex(
  pageSide: PageSide,
  pageIndex: number
): number {
  const index = pageSide === PageSide.FRONT ? pageIndex * 2 : pageIndex * 2 + 1
  return index + 1
}

export function getSinglePageProps(
  pageSide: PageSide,
  pageIndex: number
): Record<string, unknown> {
  return {
    'Page Index': calculatePageIndex(pageSide, pageIndex),
  }
}
